/*
 * -----------------------------------------------------------------------
 *  <copyright file="admin-roles-grid.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import { orderBy } from '@progress/kendo-data-query';
import React, { useEffect, useState } from 'react';
import { useTranslation, useGridCells } from 'hooks';
import { restructuredUpdatedCreatedBy } from '@utils-clean/update-users-array';
import ExpandableGrid from 'views/components/core/expandable/grid';
import { GridColumnMenuActiveFilter } from 'views/components/core/grid-filters';

import './styles.scss';

const AdminRolesGrid = ({ roles, rowActions, showPager, addScroll, options }: any) => {
    const { t } = useTranslation();
    const { YesNoCell, DateCell } = useGridCells();
    const [data, setData] = useState<unknown[]>([]);

    useEffect(() => {
        const newData = restructuredUpdatedCreatedBy(roles);
        setData(
            orderBy(newData, [
                { field: 'active', dir: 'desc' },
                { field: 'usersAmount', dir: 'desc' },
            ]),
        );
    }, [roles]);

    return (
        <ExpandableGrid
            customKey="admin-role-grid"
            id="admin-role"
            rowActions={rowActions}
            groupable={false}
            showPager={showPager}
            scrollable="scrollable"
            reorderable
            pageable={false}
            addScroll={addScroll}
            resizable
            items={data}
            options={options}
            columns={[
                {
                    label: t('common:Users'),
                    field: 'usersAmount',
                    id: 'role-users',
                    filter: 'numeric',
                    minWidth: 140,
                    lockedByDefault: true,
                },
                {
                    label: t('common:active'),
                    field: 'active',
                    id: 'role-active',
                    minWidth: 140,
                    lockedByDefault: true,
                    cells: { data: YesNoCell },
                    filterUi: GridColumnMenuActiveFilter,
                },
                {
                    label: t('role'),
                    field: 'name',
                    id: 'role-title',
                    minWidth: 150,
                    lockedByDefault: true,
                },
                {
                    label: t('common:description'),
                    field: 'description',
                    id: 'role-description',
                    minWidth: 220,
                },
                {
                    label: t('common:riskAssessmentCore'),
                    field: 'isActiveRAM',
                    id: 'role-riskAssessment',
                    minWidth: 260,
                },
                {
                    label: t('common:centralMonitoringCore'),
                    field: 'isActiveCM2',
                    id: 'role-centralMonitoring2',
                    minWidth: 300,
                },
                {
                    label: t('common:centralMonitoringCoreLegacy'),
                    field: 'isActiveCM',
                    id: 'role-centralMonitoring',
                    minWidth: 300,
                },
                {
                    label: t('common:subjectMonitoring'),
                    field: 'isActiveSM',
                    id: 'role-subjectMonitoring',
                    minWidth: 300,
                },
                {
                    label: t('common:dataQuality'),
                    field: 'isActiveDQ',
                    id: 'role-dataQuality',
                    minWidth: 300,
                },
                {
                    label: t('ra:createdBy'),
                    field: '_createdByString',
                    id: 'role-createdBy',
                    minWidth: 200,
                },
                {
                    label: t('ra:createdDate'),
                    field: 'created',
                    id: 'role-createdDate',
                    minWidth: 200,
                    cells: { data: DateCell },
                    filter: 'date',
                },
                {
                    label: t('ra:updatedBy'),
                    field: '_updatedByString',
                    id: 'role-updatedBy',
                    minWidth: 220,
                },
                {
                    label: t('ra:updatedDate'),
                    field: 'updated',
                    id: 'role-updatedDate',
                    minWidth: 210,
                    cells: { data: DateCell },
                    filter: 'date',
                },
                {
                    label: t('uam:recordId'),
                    field: 'recordId',
                    id: 'role-list-id',
                    minWidth: 100,
                    filter: 'numeric',
                    showByDefault: false,
                },
            ]}
        />
    );
};

export default AdminRolesGrid;
